import React, { useMemo } from 'react';
import classNames from 'classnames';
import { InlineSelectCell } from './InlineSelectCell';
import {
  assigneeGroups,
  assigneePatch,
  AssigneeDirectory,
  resolveActionAssignee,
  UNASSIGNED_LABEL,
} from './actionEditModel';
import { ActionAssigneeKind } from '../ICSConstants';
import type { ActionEntity } from '../hooks/useScopedActionForm';

type AssigneeCellProps = {
  action: ActionEntity;
  directory: AssigneeDirectory;
  onChange: (patch: Record<string, unknown>) => void;
  disabled?: boolean;
};

/**
 * Who the task belongs to, editable in place.
 *
 * Options are grouped by what they are, because "Operations Section Chief" and
 * "Jane Doe" answer different questions and a flat alphabetical list of both
 * hides that. The picker's ids are namespaced by kind so a team and a user that
 * happen to share an id stay distinct.
 */
export const AssigneeCell: React.FC<AssigneeCellProps> = ({
  action,
  directory,
  onChange,
  disabled = false,
}) => {
  const resolved = useMemo(
    () => resolveActionAssignee(action, directory),
    [action, directory]
  );

  const groups = useMemo(
    () =>
      assigneeGroups(directory).map((group) => ({
        label: group.label,
        items: group.items.map((item) => ({
          label: item.label,
          value: `${group.kind}:${item.value}`,
        })),
      })),
    [directory]
  );

  const value = resolved.id ? `${resolved.kind}:${resolved.id}` : null;

  const handleChange = (next: string | null) => {
    if (!next) {
      onChange(assigneePatch(null, null));
      return;
    }
    const separator = next.indexOf(':');
    const kind = next.slice(0, separator) as ActionAssigneeKind;
    onChange(assigneePatch(kind, next.slice(separator + 1)));
  };

  if (groups.length === 0) {
    return <span className="tw:text-foreground">{resolved.label}</span>;
  }

  return (
    <InlineSelectCell
      groups={groups}
      value={value}
      onChange={handleChange}
      disabled={disabled}
      ariaLabel={`Assignee for ${action.name || 'this task'}`}
      placeholder={UNASSIGNED_LABEL}
      searchPlaceholder="Search people, teams, positions"
      clearLabel={resolved.id ? UNASSIGNED_LABEL : undefined}
      contentClassName="tw:w-80! tw:min-w-80! tw:data-[chips=true]:min-w-80!"
      field="assignee"
    >
      <span
        className={classNames('text-truncate tw:text-foreground', {
          'fst-italic': resolved.unresolved,
        })}
        title={
          resolved.unresolved ? 'Assignment name not available' : undefined
        }
      >
        {resolved.label}
      </span>
    </InlineSelectCell>
  );
};

export default AssigneeCell;
